import axios from "axios";
import {ENTITY_ID_PERMISSIONS} from "./Constants";
import {API_URL} from "../../config/api.config";

export const GENERIC_GET_ALL = "/api/generic/all";
export const GENERIC_GET_ONE = "/api/generic/one";
export const GENERIC_DELETE = "/api/generic/delete";
export const GENERIC_CREATE = "/api/generic/create";
export const GENERIC_UPDATE = "/api/generic/update";
export const GENERIC_SELECT_GET_ALL = "/api/genericSelect";
export const GET_ALL_PERMISSIONS = "/api/permissions";
export const USER = "/api/users";
export const SYNC = "/api/sync";
export const SYNC_PUSH = "/api/sync/push";
export const SYNC_PULL = "/api/sync/pull";
export const SYNC_STATUS = "/api/sync/status";


export function genericGetAll(entityId, tableFilterData) {
    if (entityId === ENTITY_ID_PERMISSIONS) {
        return getAllPermissions();
    }
    return axios.get(API_URL + GENERIC_GET_ALL + "?entityId=" + entityId, {params: tableFilterData});
}

export function getAllPermissions() {
    return axios.get(API_URL + GET_ALL_PERMISSIONS);
}

export function genericGetOne(entityId, id) {
    return axios.get(API_URL + GENERIC_GET_ONE + "/" + id + "?entityId=" + entityId);
}

export function genericDelete(entityId, id) {
    return axios.delete(API_URL + GENERIC_DELETE + "/" + id + "?entityId=" + entityId);
}

export function genericSelectGetAll(entityId) {
    return axios.get(API_URL + GENERIC_SELECT_GET_ALL + "/all?entityId=" + entityId);
}

export function getSelectGetDynamicData(entityId, inputValue, searchField) {
    return axios.get(API_URL + GENERIC_SELECT_GET_ALL + "/dynamic?entityId=" + entityId + "&searchField=" + searchField + "&search=" + inputValue);
}

export function apiGenericCreate(entityId, data) {
    return axios.post(API_URL + GENERIC_CREATE + "?entityId=" + entityId, data);
}

export function apiGenericUpdate(entityId, data, id) {
    return axios.post(API_URL + GENERIC_UPDATE + "?entityId=" + entityId + "&id=" + id, data);
}

export function apiPasswordUpdate(id, data) {
    return axios.post(API_URL + USER + "/password?userName=" + id, data);
}

export function genericSync(entityId) {
    return axios.post(API_URL + SYNC + "/" + entityId);
}

export function syncPush(entityId) {
    return axios.post(API_URL + SYNC_PUSH + "/" + entityId);
}

export function syncPull(entityId) {
    return axios.post(API_URL + SYNC_PULL + "/" + entityId);
}

export function getSyncStatus(entityId) {
    return axios.get(API_URL + SYNC_STATUS + "/" + entityId);
}

// Bulk sync operations
export function getAllSyncStatus() {
    return axios.get(API_URL + SYNC_STATUS + "/all");
}

export function syncPushAll() {
    return axios.post(API_URL + SYNC_PUSH + "/all");
}

export function syncPullAll() {
    return axios.post(API_URL + SYNC_PULL + "/all");
}

export function getSyncEnabledEntities() {
    return axios.get(API_URL + SYNC + "/entities");
}

export function isSyncEnabled(entityId) {
    return axios.get(API_URL + SYNC + "/enabled/" + entityId);
}

// Organization Settings API
export const ORG_SETTINGS = "/api/org-settings";

export function getAllOrgSettings() {
    return axios.get(API_URL + ORG_SETTINGS + "/all");
}

export function updateOrgSetting(typeName, value) {
    return axios.post(API_URL + ORG_SETTINGS + "/update", { typeName, value });
}

export function getCurrentTicketNumber() {
    return axios.get(API_URL + ORG_SETTINGS + "/ticket-number/current");
}

export function getNextTicketNumber() {
    return axios.post(API_URL + ORG_SETTINGS + "/ticket-number/next");
}

export function setTicketNumber(number) {
    return axios.post(API_URL + ORG_SETTINGS + "/ticket-number/set", { number });
}

export function getCurrentPurchaseTicketNumber() {
    return axios.get(API_URL + ORG_SETTINGS + "/purchase-ticket-number/current");
}

export function getNextPurchaseTicketNumber() {
    return axios.post(API_URL + ORG_SETTINGS + "/purchase-ticket-number/next");
}

// Vehicle Data Capture API
export const VEHICLE_CAPTURE = "/api/vehicle-capture";

export function captureVehicleWeight() {
    return axios.post(API_URL + VEHICLE_CAPTURE + "/weight");
}

export function captureVehicleImage(groupId) {
    // groupId selects which setup group's camera to capture from. When omitted,
    // the backend falls back to the sole/enabled group (or the legacy single camera).
    const q = groupId ? "?groupId=" + encodeURIComponent(groupId) : "";
    return axios.post(API_URL + VEHICLE_CAPTURE + "/image" + q);
}

export function captureVehicleAll() {
    return axios.post(API_URL + VEHICLE_CAPTURE + "/all");
}

export function testCameraConnection(config) {
    return axios.post(API_URL + VEHICLE_CAPTURE + "/test-connection", config);
}

export function getDetectedSubnets() {
    return axios.get(API_URL + VEHICLE_CAPTURE + "/scan-network/subnets");
}

export function startNetworkScan(protocol, username, password, subnet, brand) {
    return axios.post(API_URL + VEHICLE_CAPTURE + "/scan-network", { protocol, username, password, subnet, brand });
}

export function getScanStatus(scanId) {
    return axios.get(API_URL + VEHICLE_CAPTURE + "/scan-network/status/" + scanId);
}

/**
 * Capture and save image for a sale/purchase entry.
 * @param {string} entryType - 'SALE' or 'PURCHASE'
 * @param {string} entryPrimaryId - Primary ID of the sale/purchase entry
 * @param {string} imageType - 'LOADED_WEIGHT' or 'EMPTY_WEIGHT'
 */
export function captureAndSaveImage(entryType, entryPrimaryId, imageType, groupId) {
    // groupId selects which setup group's camera to capture from (optional).
    return axios.post(API_URL + VEHICLE_CAPTURE + "/image/save", null, {
        params: { entryType, entryPrimaryId, imageType, ...(groupId ? { groupId } : {}) }
    });
}

/**
 * Get all captured images for a sale/purchase entry.
 * @param {string} entryType - 'SALE' or 'PURCHASE'
 * @param {string} entryPrimaryId - Primary ID of the sale/purchase entry
 */
export function getImagesForEntry(entryType, entryPrimaryId) {
    return axios.get(API_URL + VEHICLE_CAPTURE + "/images", {
        params: { entryType, entryPrimaryId }
    });
}

/**
 * Save an already-captured image (base64) for a sale/purchase entry.
 * This is used when the image was captured before the entry was created.
 * @param {string} entryType - 'SALE' or 'PURCHASE'
 * @param {string} entryPrimaryId - Primary ID of the sale/purchase entry
 * @param {string} imageType - 'LOADED_WEIGHT' or 'EMPTY_WEIGHT'
 * @param {string} base64Image - Base64 encoded image data
 * @param {string} contentType - MIME type of the image (e.g., 'image/jpeg')
 */
export function saveExistingImage(entryType, entryPrimaryId, imageType, base64Image, contentType) {
    return axios.post(API_URL + VEHICLE_CAPTURE + "/image/save-existing",
        { base64Image, contentType },
        { params: { entryType, entryPrimaryId, imageType } }
    );
}

/**
 * Get all captured images for the gallery view.
 * @returns List of all captured images
 */
export function getAllCapturedImages() {
    return axios.get(API_URL + VEHICLE_CAPTURE + "/images/all");
}

// Weighbridge Config API
export const WEIGHBRIDGE_CONFIG = "/api/weighbridge-config";

export function getWeighbridgeConfig() {
    return axios.get(API_URL + WEIGHBRIDGE_CONFIG);
}

export function saveWeighbridgeConfig(config) {
    return axios.post(API_URL + WEIGHBRIDGE_CONFIG, config);
}

export function listSerialPorts() {
    return axios.get(API_URL + WEIGHBRIDGE_CONFIG + "/serial-ports");
}

export function closeSerialPort() {
    return axios.post(API_URL + WEIGHBRIDGE_CONFIG + "/serial-close");
}

export const WEIGHBRIDGE_SERIAL_READ_URL = API_URL + WEIGHBRIDGE_CONFIG + "/serial-read";

/**
 * Build the serial-read SSE URL for a specific setup group. When groupId is
 * omitted the backend falls back to the sole/enabled group (or the legacy single
 * weighbridge config), preserving old behavior.
 */
export function weighbridgeSerialReadUrl(groupId) {
    return WEIGHBRIDGE_SERIAL_READ_URL + (groupId ? "?groupId=" + encodeURIComponent(groupId) : "");
}

// Setup Groups API — a setup group pairs ONE camera with ONE weighbridge so a
// site with multiple weighing scales/cameras can capture from a chosen pair.
export const SETUP_GROUPS = "/api/setup-groups";

// Returns the list of setup groups. On first call the backend auto-migrates any
// existing single camera + single weighbridge into a "Default" group.
export function getSetupGroups() {
    return axios.get(API_URL + SETUP_GROUPS);
}

// Create (no primaryId) or update (with primaryId) a setup group.
export function saveSetupGroup(group) {
    return axios.post(API_URL + SETUP_GROUPS, group);
}

export function deleteSetupGroup(id) {
    return axios.delete(API_URL + SETUP_GROUPS + "/" + encodeURIComponent(id));
}

/**
 * Ask the backend to generate a parser function from captured raw serial lines (via OpenRouter).
 * @param {string[]} samples - raw serial lines from the live monitor
 * @param {string} [notes] - optional hint (brand, expected weight, etc.)
 * @returns response with { parserFunction }
 */
export function generateWeighbridgeParser(samples, notes) {
    return axios.post(API_URL + WEIGHBRIDGE_CONFIG + "/generate-parser", { samples, notes });
}

// Hardware Check API — any logged-in operator. Checks each setup group's camera and
// weighbridge, and can swap two groups' weighbridge ports (nothing else).
export const HARDWARE_CHECK = "/api/hardware-check";

export function getHardwareCheckStatus() {
    return axios.get(API_URL + HARDWARE_CHECK + "/status");
}

export function runHardwareCheck() {
    return axios.post(API_URL + HARDWARE_CHECK + "/run");
}

export function swapWeighbridgePorts(groupIdA, groupIdB) {
    return axios.post(API_URL + HARDWARE_CHECK + "/swap", { groupIdA, groupIdB });
}

export function confirmWeighbridgeAdapter(groupId) {
    return axios.post(API_URL + HARDWARE_CHECK + "/confirm-adapter", { groupId });
}

// Daily Report API
export const DAILY_REPORT = "/api/daily-report";

/**
 * Get daily summary report for a specific date.
 * @param {string} date - Date in yyyy-MM-dd format
 * @returns Daily summary with sales, purchases, expenses
 */
export function getDailyReport(date) {
    // "5 Aug" is a different slice of time in every zone. The outpost forwards this
    // to the server itself, so no browser header travels with it - the zone has to
    // ride along as a parameter or the server falls back to UTC and drops the
    // early-morning tickets.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return axios.get(API_URL + DAILY_REPORT + "?date=" + date +
        (timezone ? "&timezone=" + encodeURIComponent(timezone) : ""));
}

// WiFi / Network API
export const WIFI_API = "/api/wifi";

export function getNetworkStatus() {
    return axios.get(API_URL + WIFI_API + "/network/status");
}

export function scanWifiNetworks() {
    return axios.get(API_URL + WIFI_API + "/scan");
}

export function connectWifi(ssid, password) {
    return axios.post(API_URL + WIFI_API + "/connect", { ssid, password });
}

export function disconnectWifi(ssid) {
    return axios.post(API_URL + WIFI_API + "/disconnect", { ssid });
}

// Help Document API
export function getHelpDocument(pageId, lang = 'en') {
    return axios.get(API_URL + `/api/help/page/${encodeURIComponent(pageId)}?lang=${encodeURIComponent(lang)}`);
}

export function searchHelp(query, lang = 'en') {
    return axios.get(API_URL + `/api/help/search?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(lang)}`);
}

export function getAllHelpDocuments(lang = 'en') {
    return axios.get(API_URL + `/api/help/all?lang=${encodeURIComponent(lang)}`);
}

export function getHelpLanguages() {
    return axios.get(API_URL + '/api/help/languages');
}

export function getStockYardSummary() {
    return axios.get(API_URL + "/api/stockyard/summary");
}

// GSTIN public-record lookup (Quick Fill with GST on Customer/Supplier forms)
export const GST_LOOKUP = "/api/gst-lookup";

/** Look up public GST records for a GSTIN (free sources, no captcha). */
export function lookupGstin(gstin) {
    console.log("[GstLookup] lookup request:", gstin);
    return axios
        .get(API_URL + GST_LOOKUP, { params: { gstin } })
        .then((response) => {
            console.log("[GstLookup] lookup response:", response.data);
            return response;
        })
        .catch((error) => {
            console.log("[GstLookup] lookup error:", error?.response?.data);
            throw error;
        });
}

/** Start an official-GST-portal captcha session: {sessionId, captchaImage}. */
export function getGstinCaptcha() {
    return axios
        .get(API_URL + GST_LOOKUP + "/captcha")
        .then((response) => {
            console.log("[GstLookup] captcha session:", response.data?.sessionId);
            return response;
        })
        .catch((error) => {
            console.log("[GstLookup] captcha error:", error?.response?.data);
            throw error;
        });
}

/** Complete an official-portal lookup with the user-solved captcha. */
export function verifyGstinCaptcha(payload) {
    console.log("[GstLookup] verify request:", { ...payload, captcha: "***" });
    return axios
        .post(API_URL + GST_LOOKUP + "/verify", payload)
        .then((response) => {
            console.log("[GstLookup] verify response:", response.data);
            return response;
        })
        .catch((error) => {
            console.log("[GstLookup] verify error:", error?.response?.data);
            throw error;
        });
}

/**
 * Local organization record (refreshed from the server when reachable).
 * Used to gate GST Services features like the e-Way bill switch.
 */
export function getOrgInfo() {
    return axios.get(API_URL + "/api/org-info");
}

/** Wrap an e-Invoice call with request/response logging, as the e-Way bill helpers do. */
function eInvoiceCall(label, promise) {
    return promise
        .then((response) => {
            console.log(`[EInvoice] ${label} response:`, response.data);
            return response;
        })
        .catch((error) => {
            console.log(`[EInvoice] ${label} error:`, error?.response?.data);
            throw error;
        });
}

/**
 * Retry e-Invoice registration for a synced sale (goes through the server, which owns the
 * GST portal credentials). When the sale also wanted an e-Way bill, NIC returns it in the
 * same response. Optional corrected data.
 * @param {{saleId: string, transDistance?: string, vehicleNo?: string}} payload
 */
export function retryEInvoice(payload) {
    return eInvoiceCall("retry", axios.post(API_URL + "/api/einvoice/retry", payload));
}

/** Full e-Invoice document for a synced sale (for the printable view). */
export function getEInvoiceDetail(saleId) {
    return eInvoiceCall("detail", axios.post(API_URL + "/api/einvoice/detail", { saleId }));
}

/**
 * Cancel the sale's e-Invoice on the government portal via the server.
 * @param {{saleId: string, cancelRsnCode: number, cancelRmrk: string}} payload
 */
export function cancelEInvoiceClient(payload) {
    return eInvoiceCall("cancel", axios.post(API_URL + "/api/einvoice/cancel", payload));
}

/**
 * Retry e-Way bill generation for a synced sale (goes through the server,
 * which owns the GST portal credentials). Optional corrected data.
 * @param {{saleId: string, transDistance?: string, vehicleNo?: string}} payload
 */
export function retryEwayBill(payload) {
    console.log("[EwayBill] retry request:", payload);
    return axios
        .post(API_URL + "/api/eway/retry", payload)
        .then((response) => {
            console.log("[EwayBill] retry response:", response.data);
            return response;
        })
        .catch((error) => {
            console.log("[EwayBill] retry error:", error?.response?.data);
            throw error;
        });
}

/** Full e-Way bill document for a synced sale (for the printable view). */
export function getEwayBillDetail(saleId) {
    return axios
        .post(API_URL + "/api/eway/detail", { saleId })
        .then((response) => {
            console.log("[EwayBill] detail response:", response.data);
            return response;
        })
        .catch((error) => {
            console.log("[EwayBill] detail error:", error?.response?.data);
            throw error;
        });
}

/**
 * Cancel the sale's e-Way bill on the government portal via the server.
 * @param {{saleId: string, cancelRsnCode: number, cancelRmrk: string}} payload
 */
export function cancelEwayBillClient(payload) {
    console.log("[EwayBill] cancel request:", payload);
    return axios
        .post(API_URL + "/api/eway/cancel", payload)
        .then((response) => {
            console.log("[EwayBill] cancel response:", response.data);
            return response;
        })
        .catch((error) => {
            console.log("[EwayBill] cancel error:", error?.response?.data);
            throw error;
        });
}
